import type { IconName } from '@w7/ui'

/**
 * The navigation model.
 *
 * The console this replaces had a flat rail of nine unrelated items — Member,
 * Commission, Book Management, Premium BM Management, Account Statement, All
 * Logs, Change Password, Google Authentication, Logout — mixing a daily
 * workspace with account settings and a sign-out link, and burying the five
 * report screens two levels down under "Account Statement".
 *
 * Here the rail is grouped by the question being asked:
 *   Run the book   — what is happening right now
 *   People         — who is beneath me and what are they doing
 *   Money          — what moved, what is owed, what did I earn
 * Settings and sign-out leave the rail entirely and live under the account menu.
 *
 * The owner's six named master actions each map to exactly one destination:
 *   give/take            -> Downline (row action + toolbar), and Credit transfers
 *   create masters/users -> Add to downline
 *   net exposure         -> Exposure
 *   user P&L             -> Member P&L
 *   transfer statement   -> Credit transfers
 *   my P&L               -> My P&L
 */

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  /** Matches child routes too, e.g. /exposure/market/xyz. */
  match?: string
  description: string
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

export const NAV: NavGroup[] = [
  {
    id: 'run',
    label: 'Run the book',
    items: [
      {
        id: 'overview',
        label: 'Overview',
        to: '/',
        icon: 'overview',
        description: 'Position, trend and anything needing attention',
      },
      {
        id: 'exposure',
        label: 'Exposure',
        to: '/exposure',
        icon: 'exposure',
        match: '/exposure',
        description: 'Every open market on one aligned grid, worst case first',
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      {
        id: 'downline',
        label: 'Downline',
        to: '/downline',
        icon: 'downline',
        match: '/downline',
        description: 'Direct children, the whole tree, and Monday’s bulk settle',
      },
      {
        id: 'create',
        label: 'Add to downline',
        to: '/create',
        icon: 'plus',
        description: 'Create an agent or a member — one page, everything visible',
      },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      {
        id: 'my-pnl',
        label: 'My P&L',
        to: '/reports/my-pnl',
        icon: 'pnl',
        description: 'What the book earned you, after shares and commission',
      },
      {
        id: 'member-pnl',
        label: 'Member P&L',
        to: '/reports/member-pnl',
        icon: 'users',
        description: 'Who is winning and losing beneath you',
      },
      {
        id: 'transfers',
        label: 'Credit transfers',
        to: '/reports/transfers',
        icon: 'transfers',
        description: 'Every give and take, with running balances',
      },
      {
        id: 'turnover',
        label: 'Turnover',
        to: '/reports/turnover',
        icon: 'turnover',
        description: 'Matched volume by product, sport and agent',
      },
      {
        id: 'commission',
        label: 'Commission',
        to: '/reports/commission',
        icon: 'commission',
        description: 'Commission earned and paid down the chain',
      },
    ],
  },
  {
    id: 'trail',
    label: 'Trail',
    items: [
      {
        id: 'activity',
        label: 'Activity',
        to: '/activity',
        icon: 'activity',
        description: 'Who did what, when, from where',
      },
    ],
  },
]

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((group) => group.items)
